import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Input, Message, Icon, Grid } from 'semantic-ui-react';

import { PerdiemsStore, PerdiemDto } from 'stores/configuration/perdiems/perdiems-store';
import { ItemState, Query, OrderDefinition } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import NewPerdiemView from './new-perdiem';
import ChangePerdiemView from './edit-perdiem';

export interface PerdiemsListProps extends WithTranslation, RouteComponentProps {
  perdiems: PerdiemsStore;
}

export interface PerdiemsListState {
  query: Query;
  newPerdiemShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  changePerdiemShown: boolean;
  someFilterOpened: boolean;
}

@connect(['perdiems', PerdiemsStore])
class PerdiemsListPage extends React.Component<PerdiemsListProps, PerdiemsListState> {
  handleOnEnterKeydown = item => {
    this.setState({ selectedItem: item }, () => this.onEditItem());
  };

  handleOnRowDoubleClick = item => {
    this.handleOnEnterKeydown(item);
  };

  constructor(props) {
    super(props);
    this.state = {
      query: {
        searchQuery: '',
        orderBy: [{ direction: 'Descending', field: nameof<PerdiemDto>('modifiedOn'), useProfile: false }],
        skip: 0,
        take: 10
      },
      newPerdiemShown: false,
      activeFilters: [],
      selectedItem: null,
      changePerdiemShown: false,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.perdiems.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState({ query: Object.assign(this.state.query, { orderBy }) }, () => {
      this.load();
    });
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) }, () => {
      this.load();
    });
  }

  @autobind
  private async onSaveRow(item: PerdiemDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.perdiems.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: PerdiemDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.perdiems.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newPerdiemShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newPerdiemShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changePerdiemShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changePerdiemShown: true });
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Location'),
          tooltipRenderer: true,
          renderer: data => <span>&nbsp;{data.locationName}</span>,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by from location')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<PerdiemDto>('locationName'), `contains(tolower(${'Location/Location'}), '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<PerdiemDto>('locationName'))}
              active={activeFilters.includes(nameof<PerdiemDto>('locationName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'Location/Location',
            useProfile: false
          }
        },
        {
          title: t('Per Diem'),
          tooltipRenderer: true,
          renderer: data => data.perdiem,
          editor: (data, onChange) => (
            <Input
              type="number"
              value={data.perdiem}
              fluid
              onChange={(e, { value }) => {
                data.perdiem = Number(value);
                onChange();
              }}
            />
          ),
          selectableHeader: true
        },
        {
          title: t('Hardship'),
          tooltipRenderer: true,
          renderer: data => data.hardship,
          editor: (data, onChange) => (
            <Input
              type="number"
              value={data.hardship}
              fluid
              onChange={(e, { value }) => {
                data.hardship = Number(value);
                onChange();
              }}
            />
          ),
          selectableHeader: true
        },
        {
          title: t('Currency'),
          tooltipRenderer: true,
          renderer: data => <span>&nbsp;{data.currency}</span>,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by from Currency')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<PerdiemDto>('currency'), `contains(${nameof<PerdiemDto>('currency')}, '${value}')`)}
              onClear={() => onClear(nameof<PerdiemDto>('currency'))}
              active={activeFilters.includes(nameof<PerdiemDto>('currency'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<PerdiemDto>('currency'),
            useProfile: false
          }
        }
      ],
      data: this.props.perdiems.state
    } as TableModel<PerdiemDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.perdiems.state.result && !this.props.perdiems.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.perdiems.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newPerdiemShown && !this.state.changePerdiemShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Perdiem')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newPerdiemShown && <NewPerdiemView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changePerdiemShown && <ChangePerdiemView onClose={this.onEditItemClosed} currentPerdiem={this.state.selectedItem} />)}
      </>
    );
  }
}

export default withTranslation()(PerdiemsListPage);
